import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import {
  LogOut, Menu, Shield, Database, Cookie, Lock, UserCheck,
  Mail, Globe, FileText, AlertTriangle, RefreshCw, Phone,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import LanguageSelector from '../components/LanguageSelector';
import ThemeToggle from '../components/ThemeToggle';
import MobileMenu from '../components/MobileMenu';
import SiteFooter from '../components/SiteFooter';
import HreflangTags from '../components/HreflangTags';
import { PRIVACY_CONTENT } from '../i18n/privacyContent';
import '../styles/PrivacyPage.css';
import '../styles/HomePage.css';

const LOGO_DARK = "https://customer-assets.emergentagent.com/job_422444ca-8f0f-4ebc-8f78-bee66ce7f723/artifacts/qo6wuzuf_REGISTAN%20LOGO.png";
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_9d222022-9462-4aac-aca8-59da15d4f381/artifacts/tueqptuk_registan%20logo.png";

const SECTION_DEFS = [
  { id: 'introduction', key: 'introduction', icon: Shield },
  { id: 'data-we-collect', key: 'dataCollect', icon: Database },
  { id: 'how-we-use', key: 'howWeUse', icon: UserCheck },
  { id: 'cookies', key: 'cookies', icon: Cookie },
  { id: 'data-sharing', key: 'sharing', icon: Globe },
  { id: 'data-security', key: 'security', icon: Lock },
  { id: 'data-retention', key: 'retention', icon: FileText },
  { id: 'your-rights', key: 'rights', icon: UserCheck },
  { id: 'children', key: 'children', icon: AlertTriangle },
  { id: 'changes', key: 'changes', icon: RefreshCw },
  { id: 'contact', key: 'contact', icon: Mail },
];

const PrivacyPage = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const { t, language } = useLanguage();
  const { theme } = useTheme();
  const currentLogo = theme === 'light' ? LOGO_LIGHT : LOGO_DARK;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('introduction');

  // Pick localized content (fallback to EN)
  const C = PRIVACY_CONTENT[language] || PRIVACY_CONTENT.en;

  const dateLocale = language === 'ru' ? 'ru-RU' : language === 'uz' ? 'uz-UZ' : 'en-US';
  const lastUpdated = useMemo(() => {
    return new Date('2026-02-06').toLocaleDateString(dateLocale, {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  }, [dateLocale]);

  const handleNavClick = (id) => {
    setActiveSection(id);
    const el = document.getElementById(`pp-${id}`);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 90;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <div className="privacy-page" data-testid="privacy-page">
      <Helmet>
        <title>{C.badge} | Registan Tourism — Dubai Visa Services</title>
        <meta name="description" content={C.pageSub} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://registantours.com/privacy" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://registantours.com/privacy" />
        <meta property="og:title" content={`${C.badge} — Registan Tourism`} />
        <meta property="og:site_name" content="Registan Tourism" />
        <meta name="twitter:card" content="summary" />
      </Helmet>
      <HreflangTags path="/privacy" />

      {/* Header — simplified (no Visa Status / Transactions / Chat AI icons) */}
      <header className="dark-header">
        <div className="header-left">
          <div className="header-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img src={currentLogo} alt="Registan Tourism" className="logo-img" />
          </div>
          <LanguageSelector />
          <ThemeToggle />
        </div>
        <nav className="dark-nav desktop-hide-nav">
          <Link to="/" className="dark-nav-link">{t('home') || 'Home'}</Link>
          <Link to="/tours" className="dark-nav-link">{t('tours') || 'Tours'}</Link>
          <Link to="/start-business" className="dark-nav-link">{t('startBusiness') || 'Start Business'}</Link>
          <Link to="/about" className="dark-nav-link">{t('about') || 'About'}</Link>
        </nav>
        {isAuthenticated ? (
          <div className="header-user-menu">
            <button className="btn-user-profile" onClick={() => navigate('/dashboard')}>
              <div className="user-avatar-small">{user?.name?.charAt(0)?.toUpperCase()}</div>
              <span>{user?.name}</span>
            </button>
            <button className="btn-logout-header" onClick={logout} title="Logout">
              <LogOut size={20} />
            </button>
          </div>
        ) : (
          <div className="header-auth-buttons">
            <button className="btn-secondary btn-login" onClick={() => navigate('/login')}>{t('login') || 'Login'}</button>
            <button className="btn-primary btn-signup" onClick={() => navigate('/register')}>{t('signUp') || 'SIGN UP'}</button>
          </div>
        )}
        <button className="mobile-menu-toggle" onClick={() => setIsMobileMenuOpen(true)}>
          <Menu size={24} />
        </button>
      </header>

      <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />

      {/* Hero */}
      <section className="pp-hero">
        <div className="pp-hero-glow pp-hero-glow-1" />
        <div className="pp-hero-glow pp-hero-glow-2" />
        <div className="pp-hero-inner">
          <div className="pp-hero-badge">
            <Shield size={14} />
            <span>{C.badge}</span>
          </div>
          <h1 data-testid="privacy-h1">{C.pageTitle}</h1>
          <p className="pp-hero-sub">{C.pageSub}</p>
          <p className="pp-hero-meta">{C.lastUpdated}: {lastUpdated}</p>
        </div>
      </section>

      {/* Layout: Left sidebar TOC + content */}
      <div className="pp-layout">
        <aside className="pp-sidebar" aria-label="Privacy policy sections">
          <div className="pp-sidebar-title">{C.onThisPage}</div>
          <ul className="pp-toc">
            {SECTION_DEFS.map(({ id, key, icon: Icon }) => (
              <li key={id}>
                <button
                  type="button"
                  className={`pp-toc-link ${activeSection === id ? 'active' : ''}`}
                  onClick={() => handleNavClick(id)}
                  data-testid={`privacy-toc-${id}`}
                >
                  <Icon size={14} />
                  <span>{C.sectionLabels[key]}</span>
                </button>
              </li>
            ))}
          </ul>
          <div className="pp-sidebar-card">
            <h4>{C.needHelp}</h4>
            <p>{C.needHelpSub}</p>
            <a href="mailto:support@registantours.com" className="pp-sidebar-cta">
              <Mail size={14} /> support@registantours.com
            </a>
            <a href="tel:+971569130707" className="pp-sidebar-cta">
              <Phone size={14} /> +971 56 913 0707
            </a>
          </div>
        </aside>

        <main className="pp-content">
          {/* 1. Introduction */}
          <section id="pp-introduction" className="pp-section">
            <h2><Shield size={20} /> {C.introduction.title}</h2>
            {C.introduction.body.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </section>

          {/* 2. Data We Collect */}
          <section id="pp-data-we-collect" className="pp-section">
            <h2><Database size={20} /> {C.dataCollect.title}</h2>
            <p>{C.dataCollect.intro}</p>
            <ul>
              {C.dataCollect.items.map(([term, desc], i) => (
                <li key={i}><strong>{term}</strong> — {desc}</li>
              ))}
            </ul>
          </section>

          {/* 3. How We Use */}
          <section id="pp-how-we-use" className="pp-section">
            <h2><UserCheck size={20} /> {C.howWeUse.title}</h2>
            <p>{C.howWeUse.intro}</p>
            <ul>
              {C.howWeUse.items.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
            <p>{C.howWeUse.footer}</p>
          </section>

          {/* 4. Cookies */}
          <section id="pp-cookies" className="pp-section">
            <h2><Cookie size={20} /> {C.cookies.title}</h2>
            {C.cookies.body.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </section>

          {/* 5. Sharing */}
          <section id="pp-data-sharing" className="pp-section">
            <h2><Globe size={20} /> {C.sharing.title}</h2>
            <p>{C.sharing.intro}</p>
            <ul>
              {C.sharing.items.map(([term, desc], i) => (
                <li key={i}><strong>{term}</strong> — {desc}</li>
              ))}
            </ul>
          </section>

          {/* 6. Security */}
          <section id="pp-data-security" className="pp-section">
            <h2><Lock size={20} /> {C.security.title}</h2>
            <p>{C.security.intro}</p>
            <ul>
              {C.security.items.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
            <p>{C.security.footer}</p>
          </section>

          {/* 7. Retention */}
          <section id="pp-data-retention" className="pp-section">
            <h2><FileText size={20} /> {C.retention.title}</h2>
            <p>{C.retention.intro}</p>
            <ul>
              {C.retention.items.map(([term, desc], i) => (
                <li key={i}><strong>{term}</strong> — {desc}</li>
              ))}
            </ul>
            <p>{C.retention.footer}</p>
          </section>

          {/* 8. Rights */}
          <section id="pp-your-rights" className="pp-section">
            <h2><UserCheck size={20} /> {C.rights.title}</h2>
            <p>{C.rights.intro}</p>
            <ul>
              {C.rights.items.map(([term, desc], i) => (
                <li key={i}><strong>{term}</strong> — {desc}</li>
              ))}
            </ul>
            <p>{C.rights.footer}</p>
          </section>

          {/* 9. Children */}
          <section id="pp-children" className="pp-section">
            <h2><AlertTriangle size={20} /> {C.children.title}</h2>
            {C.children.body.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </section>

          {/* 10. Changes */}
          <section id="pp-changes" className="pp-section">
            <h2><RefreshCw size={20} /> {C.changes.title}</h2>
            {C.changes.body.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </section>

          {/* 11. Contact */}
          <section id="pp-contact" className="pp-section pp-contact">
            <h2><Mail size={20} /> {C.contact.title}</h2>
            <p>{C.contact.intro}</p>
            <ul className="pp-contact-list">
              <li><Mail size={14} /> <a href="mailto:support@registantours.com">support@registantours.com</a></li>
              <li><Phone size={14} /> <a href="tel:+971569130707">+971 56 913 0707</a></li>
              <li><Globe size={14} /> <a href="https://registantours.com" target="_blank" rel="noopener noreferrer">registantours.com</a></li>
            </ul>
          </section>
        </main>
      </div>

      <SiteFooter />
    </div>
  );
};

export default PrivacyPage;
